import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Upload, ListChecks } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import CsvImportDialog from "@/components/CsvImportDialog";

export default function NdisCodesSettings() {
  const [rows, setRows] = useState([]);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    try { setRows((await api.get("/ndis-codes/custom")).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this custom code?")) return;
    try {
      await api.delete(`/ndis-codes/custom/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeAll = async () => {
    if (!window.confirm("Clear ALL of your custom NDIS codes?")) return;
    try {
      const r = await api.delete("/ndis-codes/custom");
      toast.success(`Cleared ${r.data.deleted} code${r.data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="ndis-codes-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-client" />
            <h3 className="font-display font-extrabold text-xl">Custom NDIS codes</h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            Add codes from your own service agreement that aren't in the built-in catalogue. They show up
            on every code dropdown alongside the standard ones.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setImportOpen(true)} className="rounded-full font-bold gap-2"
                  style={{ background: "hsl(var(--client))" }} data-testid="ndis-import-btn">
            <Upload className="w-4 h-4" /> Import CSV
          </Button>
          {rows.length > 0 && (
            <Button onClick={removeAll} variant="outline" className="rounded-full font-bold text-destructive border-destructive/30"
                    data-testid="ndis-clear-btn">
              Clear all
            </Button>
          )}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed p-8 text-center text-muted-foreground" data-testid="ndis-empty-state">
          You haven't added any custom codes yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border-2">
          <table className="w-full text-sm" data-testid="ndis-codes-table">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider font-bold">
              <tr>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Task</th>
                <th className="text-left p-3">Rate type</th>
                <th className="text-left p-3">Description</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`ndis-row-${r.id}`}>
                  <td className="p-3 font-mono font-bold">{r.code}</td>
                  <td className="p-3 capitalize">{r.task || "—"}</td>
                  <td className="p-3 capitalize">{(r.rate_type || "").replace(/_/g, " ") || "—"}</td>
                  <td className="p-3">{r.description || "—"}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" className="rounded-full text-destructive"
                            onClick={() => remove(r.id)} data-testid={`ndis-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CsvImportDialog
        open={importOpen} onOpenChange={setImportOpen}
        title="Import custom NDIS codes"
        description="One row per code. Codes are merged with the built-in catalogue."
        templateUrl="/ndis-codes/custom/template"
        importUrl="/ndis-codes/import"
        templateFilename="ndis_codes_template.csv"
        testIdPrefix="ndis-import"
        onDone={() => load()}
      />
    </Card>
  );
}
